import { useMemo, useState } from 'react';
import type { Prayer, EncouragementNote } from '@flock-types/prayer.types';
import { timeAgo, timestampMillis } from '@/lib/timeAgo';
import EncouragementReactions from './EncouragementReactions';

function noteMillis(value: EncouragementNote['createdAt']): number {
  const millis = timestampMillis(value as any);
  return Number.isFinite(millis) ? millis : 0;
}

const PREVIEW_COUNT = 2;

interface Props {
  prayer: Prayer;
  /** True = show all notes + composer; false = latest 2 preview only. */
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onSubmit: (note: string) => Promise<void>;
  onReactionToggle: (noteId: string, emoji: string, reacted: boolean) => void;
}

/**
 * Encouragement notes on a prayer card. The latest two notes are ALWAYS
 * visible (no click required — encouragement should be seen immediately);
 * expanding reveals the full thread plus the composer.
 */
export default function EncouragementSection({
  prayer,
  expanded,
  onExpandChange,
  onSubmit,
  onReactionToggle,
}: Props) {
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);

  const notes = useMemo(
    () => [...prayer.encouragementNotes].sort((a, b) => noteMillis(a.createdAt) - noteMillis(b.createdAt)),
    [prayer.encouragementNotes]
  );
  const visibleNotes = expanded ? notes : notes.slice(-PREVIEW_COUNT);
  const hiddenCount = notes.length - visibleNotes.length;

  if (notes.length === 0 && !expanded) return null;

  const submitNote = async () => {
    if (!noteText.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(noteText.trim());
      setNoteText('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-flock-gray-100 pt-3">
      {hiddenCount > 0 && (
        <button
          onClick={() => onExpandChange(true)}
          className="mb-2 text-xs font-medium text-flock-navy-500 hover:text-flock-navy-700"
        >
          View all {notes.length} encouragements
        </button>
      )}

      {visibleNotes.map((note, i) => {
        // Reactions are addressed by note id — disable for notes that only
        // exist optimistically (temp-) or predate ids in very old documents.
        const noteId = note.id ?? '';
        const reactable = Boolean(noteId) && !noteId.startsWith('temp-');
        return (
          <div key={noteId || `note-${i}`} className="group mb-2 rounded-lg bg-flock-navy-50 px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-semibold text-flock-navy-700">{note.userName}</span>
              <span className="shrink-0 text-[10px] text-flock-navy-400">
                {timeAgo(note.createdAt as any)}
              </span>
            </div>
            <div className="text-sm text-flock-navy-800">{note.note}</div>
            <EncouragementReactions
              reactions={noteId ? prayer.encouragementReactions?.[noteId] : undefined}
              disabled={!reactable}
              onToggle={(emoji, reacted) => onReactionToggle(noteId, emoji, reacted)}
            />
          </div>
        );
      })}

      {expanded && (
        <div className="flex gap-2">
          <input
            autoFocus
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitNote();
            }}
            placeholder="Write a word of encouragement…"
            className="min-w-0 flex-1 rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
          />
          <button
            onClick={submitNote}
            disabled={busy || !noteText.trim()}
            className="rounded-lg bg-flock-navy-600 px-3 py-2 text-xs font-medium text-white hover:bg-flock-navy-700 disabled:opacity-50"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
